import { Icon } from 'Components/shared/Icon';
import { IconButton } from 'Components/shared/IconButton';
import { ListPopover } from 'Components/shared/ListPopover';
import { TopBarDropdownContext } from 'Components/TopBar/contexts/TopBarDropdownContext';
import { COMMUNICATOR_CREATE_CONVERSATION_V2 } from 'Constants/featureFlags';
import { ROUTE_PATHS } from 'Constants/routing';
import useFeatureFlags from 'Hooks/featureFlags/useFeatureFlags';
import { MobXProviderContext } from 'mobx-react';
import { useConversationStore } from 'Modules/conversation/store/index.store';
import { useUIStore } from 'Modules/ui/index.store';
import { useIsSecondaryMenuVisible } from 'Modules/ui/secondaryMenu/hooks/useIsSecondaryMenuVisible';
import React from 'react';
import { useNavigate } from 'react-router';
import { RootStoreProps } from 'Stores/RootStore.types';
import { pushToGTMDataLayer } from 'Utils/analytics';
import { PopoverAnchor } from '~/lib/ui/popover';
import { cn } from '~/lib/utils';
import { useConversationsFilter } from './hooks';

const NEW_CONVERSATION_PATH = ROUTE_PATHS.newConversation.path;

const ConversationsPanelHeader: React.FC = () => {
  const navigate = useNavigate();

  const {
    [COMMUNICATOR_CREATE_CONVERSATION_V2]: isCreateConversationV2Enabled,
  } = useFeatureFlags<boolean>([COMMUNICATOR_CREATE_CONVERSATION_V2]);

  const {
    ui: {
      setSelectedTopBarUsers,
      setTopBarContactPageNumber,
      setTopBarSearchValue,
    },
    conversation: { setChannelInfoDetails },
    message,
  } = React.useContext<RootStoreProps>(MobXProviderContext);
  const isSecondaryMenuVisible = useIsSecondaryMenuVisible();
  const { setSelection } = React.useContext(TopBarDropdownContext);

  const { resetAutocomplete } = useConversationStore();

  const { resetEntitySidePanel } = useUIStore();

  const { options, currentFilterLabel, isPopoverOpen, setIsPopoverOpen } =
    useConversationsFilter();

  const clearTopBarDropdownAndSidebar = () => {
    // Clear the selected contacts from the top bar dropdown
    setSelection({ users: [], variant: null });
    setSelectedTopBarUsers(null);
    setTopBarContactPageNumber(1);
    setTopBarSearchValue('');

    // Clear the message draft on the message input
    message.setMessageDraftRaw('', '');
    message.setMessageDraftHtml('', '');

    // Clear the contact options and preview conversation ID
    resetAutocomplete();

    // TODO: Temporary solution — will be replaced by facade methods that handle the new sidebar
    setChannelInfoDetails('0', 'new');
    resetEntitySidePanel();
  };

  const handleNewConversation = () => {
    pushToGTMDataLayer('chat_list_start_chat');

    clearTopBarDropdownAndSidebar();

    void navigate(NEW_CONVERSATION_PATH);
  };

  return (
    <div
      className={cn(
        'flex justify-between items-center pl-[15px] pr-[20px] pt-[14px] pb-[6px]'
      )}
    >
      <ListPopover
        side="bottom"
        options={options}
        arrowOffset={1}
        align="start"
        open={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        hidden={!isSecondaryMenuVisible}
        avoidCollisions={false}
      >
        <div
          className={cn(
            'flex items-center gap-[5px] text-[16px] text-start text-foreground leading-[22px] font-normal cursor-pointer'
          )}
          onClick={() => setIsPopoverOpen(!isPopoverOpen)}
        >
          {/* The PopoverAnchor is needed so the arrow points to the icon */}
          <PopoverAnchor>
            <Icon
              icon="filter"
              className="flex items-end size-[24px]"
              testId="filter-icon"
            />
          </PopoverAnchor>

          {currentFilterLabel}
        </div>
      </ListPopover>

      {isCreateConversationV2Enabled && (
        <IconButton
          onClick={handleNewConversation}
          icon="chat-group"
          size="medium"
          variant="secondary"
          testid="new-conversation-button"
        />
      )}
    </div>
  );
};

export default ConversationsPanelHeader;
